import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import * as memory from 'utils/memory';
import { isDateInFuture } from 'utils/date';
import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { CURRENT_FISCAL_YEAR } from 'constants/memory';
import { fetch as fetchFiscalYear } from 'services/fiscalYear';
import { useCurrentUser } from 'hooks/query/useUserInfo';

interface FiscalYear {
  id?: string;
  country?: string;
  endDate?: string;
  isCurrent?: boolean;
  [key: string]: string | number | boolean | null | undefined;
}

const useCurrentFiscalYear = (): FiscalYear => {
  const user = useCurrentUser();
  const fiscalYearFromLocalStorage = useMemo(() => memory.get(CURRENT_FISCAL_YEAR, true, {}) as FiscalYear, []);
  const storedFiscalYearCountry = fiscalYearFromLocalStorage.country;
  const storedFiscalYearEndDate = fiscalYearFromLocalStorage.endDate;

  const isStoredFiscalYearCurrent =
    !isObjectEmpty(fiscalYearFromLocalStorage) &&
    Boolean(storedFiscalYearEndDate) &&
    isDateInFuture(String(storedFiscalYearEndDate)) &&
    storedFiscalYearCountry === user?.country;

  const [currentFiscalYear, setCurrentFiscalYear] = useState<FiscalYear>(
    isStoredFiscalYearCurrent ? fiscalYearFromLocalStorage : {}
  );
  const resolvedCurrentFiscalYear =
    isStoredFiscalYearCurrent && isObjectEmpty(currentFiscalYear) ? fiscalYearFromLocalStorage : currentFiscalYear;

  useEffect(() => {
    if (isStoredFiscalYearCurrent) {
      return;
    }

    const fetchCurrentFiscalYear = async (): Promise<void> => {
      try {
        if (!user?.country) {
          return;
        }

        const fiscalYears = (await fetchFiscalYear({ country: user.country })) as FiscalYear[];
        const newCurrentFiscalYear = fiscalYears.find(({ isCurrent }) => isCurrent) || {};

        if (!isEqual(currentFiscalYear, newCurrentFiscalYear)) {
          memory.set(CURRENT_FISCAL_YEAR, newCurrentFiscalYear);
          setCurrentFiscalYear(newCurrentFiscalYear);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchCurrentFiscalYear();
  }, [
    currentFiscalYear,
    fiscalYearFromLocalStorage,
    isStoredFiscalYearCurrent,
    storedFiscalYearCountry,
    storedFiscalYearEndDate,
    user?.country,
  ]);

  return resolvedCurrentFiscalYear;
};

export default useCurrentFiscalYear;
